import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Dayjs } from 'dayjs';

export interface Tariff {
  id: number;
  from: string | null;
  to: string | null;
  transport_type: {
    id: number;
    name: string;
  };
  price: number;
  origin_port: {
    id: number;
    name: string;
    country: string | null;
  };
  destination_port: {
    id: number;
    name: string;
    country: string | null;
  };
  bookings_type: {
    id: number;
    name: string;
  } | null;
  tariff_bracket: {
    id: number;
    from: number | null;
    to: number | null;
  } | null;
  container_size: {
    id: number;
    name: string;
  } | null;
}

interface RawTariffResponse {
  id: number;
  from: string | null;
  to: string | null;
  price: number;
  transport_type: number;
  origin_port: number;
  destination_port: number;
  bookings_type: number | null;
  tariff_bracket: number | null;
  created_at?: string;
  transport_type_data: {
    id: number;
    name: string;
  };
  origin_port_data: {
    id: number;
    name: string;
    country: string | null;
  };
  destination_port_data: {
    id: number;
    name: string;
    country: string | null;
  };
  bookings_type_data: {
    id: number;
    name: string;
  } | null;
  tariff_bracket_data: {
    id: number;
    from: number | null;
    to: number | null;
  } | null;
  container_size_data: {
    id: number;
    name: string;
  } | null;
}

interface UseTariffsWithHistoryProps {
  startDate?: Dayjs;
  endDate?: Dayjs;
  includeHistorical?: boolean;
}

export const useTariffsWithHistory = ({ 
  startDate, 
  endDate, 
  includeHistorical = false 
}: UseTariffsWithHistoryProps = {}) => {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTariffs = useCallback(async () => {
    try {
      setLoading(true);

      let query = supabase
        .from("tariffs")
        .select(`
          *,
          transport_type_data:booking_transport_types(*),
          origin_port_data:booking_origin_port(*),
          destination_port_data:booking_destination_port(*),
          tariff_bracket_data:tariff_brackets(*),
          container_size_data:container_size(*),
          bookings_type_data:bookings_type(*)
        `);

      // Apply date filtering if dates are provided
      if (startDate && endDate) {
        const startDateStr = startDate.format('YYYY-MM-DD');
        const endDateStr = endDate.format('YYYY-MM-DD');
        
        if (includeHistorical) {
          // For historical view: include tariffs that were active during the period
          // This includes tariffs that:
          // 1. Started before/during the period AND ended after/during the period
          // 2. OR have no end date (ongoing) AND started before/during the period
          // 3. OR have no start date (legacy) AND ended after/during the period
          query = query.or(
            `and(from.lte.${endDateStr},to.gte.${startDateStr}),` +
            `and(from.lte.${endDateStr},to.is.null),` +
            `and(from.is.null,to.gte.${startDateStr}),` +
            `and(from.is.null,to.is.null)`
          );
        } else {
          // For current view: only active tariffs (no end date or end date in future)
          query = query.or(`to.is.null,to.gte.${startDateStr}`);
        }
      } else if (!includeHistorical) {
        // Default behavior: only show current/future tariffs
        const today = new Date().toISOString().split('T')[0];
        query = query.or(`to.is.null,to.gte.${today}`);
      }

      query = query.order('created_at', { ascending: false });

      const { data: response, error } = await query;

      if (error) {
        console.error('Tariffs query error:', error);
        throw error;
      }

      console.log('Tariffs query response:', response ? 'Data received' : 'No data', 
                 'Count:', Array.isArray(response) ? response.length : 0);
      console.log('Date range:', startDate?.format('YYYY-MM-DD'), 'to', endDate?.format('YYYY-MM-DD'));
      console.log('Include historical:', includeHistorical);

      if (!response || !Array.isArray(response)) {
        console.warn('Tariffs response is not an array:', response);
        setTariffs([]);
        return;
      }

      // Transform the response to match the expected interface
      const transformedTariffs: Tariff[] = response.map((item: RawTariffResponse) => ({
        id: item.id,
        from: item.from,
        to: item.to,
        price: item.price,
        transport_type: item.transport_type_data,
        origin_port: item.origin_port_data,
        destination_port: item.destination_port_data,
        bookings_type: item.bookings_type_data,
        tariff_bracket: item.tariff_bracket_data,
        container_size: item.container_size_data,
      }));

      setTariffs(transformedTariffs);
      setError(null);
    } catch (err) {
      console.error('Error fetching tariffs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tariffs');
      setTariffs([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, includeHistorical]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  return { tariffs, loading, error, refetch: fetchTariffs };
};
